import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Banner, Button } from 'slab'
import {
  getGetBrokersTasksQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  usePatchBrokersTasks,
} from 'utils/web_api'
import type { BrokersTask } from 'utils/web_api.schemas'

import { BLOCKED_REASON_LABELS } from './BlockTaskDialog'

interface UnblockTaskDialogProps {
  task: BrokersTask
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const UnblockTaskDialog = ({
  task,
  isOpen,
  onClose,
  onSuccess,
}: UnblockTaskDialogProps) => {
  const [unblockReason, setUnblockReason] = useState<string>('')
  const queryClient = useQueryClient()

  const unblockMutation = usePatchBrokersTasks()

  const handleUnblock = () => {
    const message = unblockReason.trim() || 'Unblocked by broker via UI'

    unblockMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          status_event: 'unblock',
          status_message: message,
          unblock_reason: unblockReason.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all task-related queries to refresh the UI
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksTaskIdQueryKey(task.id),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsQueryKey(),
          })

          toast.success('Task unblocked successfully')
          onSuccess?.()
          handleClose()
        },
        onError: (error) => {
          console.error('Failed to unblock task:', error)

          // Try to extract error message from the response
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to unblock task. Please try again.'

          toast.error(errorMessage)

          // Invalidate queries anyway in case the task was actually updated
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksTaskIdQueryKey(task.id),
          })
        },
      }
    )
  }

  const handleClose = () => {
    setUnblockReason('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Unblock Task</DialogTitle>
          <DialogDescription>
            Resume processing this task by removing the block.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {task.blocked_reason && (
            <div className='rounded-md bg-gray-50 p-3 border border-gray-200'>
              <p className='text-sm text-gray-600 mt-1'>
                <strong>Reason:</strong> {BLOCKED_REASON_LABELS[task.blocked_reason]}
                <div>{task.message}</div>
              </p>
              {task.blocked_at && (
                <p className='text-xs text-gray-500 mt-2'>
                  Blocked on {new Date(task.blocked_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <div className='space-y-2'>
            <Label htmlFor='unblock-reason'>Resolution Details (Optional)</Label>
            <Input
              id='unblock-reason'
              value={unblockReason}
              onChange={(e) => setUnblockReason(e.target.value)}
              placeholder='E.g., Member provided missing documentation'
              className='w-full'
            />
            <p className='text-sm text-gray-500'>
              Explain what was done to resolve the blocker
            </p>
          </div>

          <Banner
            title='Unblocked tasks will be processed again'
            type='notice'
          >
            The task will transition back to a pending status and
            may be automatically executed if it's an automated task.
          </Banner>

        </div>

        <DialogFooter>
          <Button variant='secondary' onClick={handleClose} disabled={unblockMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleUnblock}
            disabled={unblockMutation.isPending}
          >
            {unblockMutation.isPending ? 'Unblocking...' : 'Unblock Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
